import { apiErrorMessage } from '@agos/api-client'
import { T, tt } from '@agos/shared'
import { cn, toast } from '@agos/ui'
import { BellRing } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { KITCHEN_CALL_HOLD_MS } from './kitchen-calls'
import { postKitchenCall } from './kitchen-calls-api'

/**
 * The kitchen's bell - FRONTEND.md §9.720, §9.721, owner O95.
 *
 * <p>On a ticket (`orderId`) it calls a waiter about that order; in the header (`orderId` null) it is the general
 * bell. A press posts the call, says *Čašník privolaný* and holds the button for five seconds from the press, so a
 * double tap rings once.
 *
 * <p><b>A failed post says the server's sentence and lets go at once</b>: a call that did not go through is one the
 * cook should be able to make again straight away, not five seconds later.
 *
 * <p><b>The hold is the `disabled` attribute and nothing else.</b> A ref beside it was written first, for two taps
 * landing before the first re-render, and removed: a click is a discrete event, React commits its state before the
 * next one is dispatched, and no case could tell the ref from its absence (§9.721's mutations).
 */
export function KitchenCallButton({ orderId, className }: { orderId: number | null; className?: string }) {
  const [held, setHeld] = useState(false)
  const release = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (release.current !== null) clearTimeout(release.current)
    },
    [],
  )

  const letGo = () => {
    if (release.current !== null) clearTimeout(release.current)
    release.current = null
    setHeld(false)
  }

  const press = async () => {
    setHeld(true)
    release.current = setTimeout(letGo, KITCHEN_CALL_HOLD_MS)
    try {
      await postKitchenCall(orderId)
      toast({ title: tt('Čašník privolaný') })
    } catch (caught) {
      letGo()
      toast({
        variant: 'destructive',
        title: tt('Čašníka sa nepodarilo privolať'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    }
  }

  const general = orderId === null
  return (
    <button
      type="button"
      onClick={() => void press()}
      disabled={held}
      aria-label={general ? tt('Zvonček - zavolať čašníka') : tt('Zavolať čašníka k tejto objednávke')}
      title={general ? tt('Zvonček - zavolať čašníka') : tt('Zavolať čašníka k tejto objednávke')}
      {...(general ? { 'data-kitchen-call-general': '' } : { 'data-kitchen-call': orderId })}
      data-kitchen-call-held={held ? 'true' : 'false'}
      className={cn(
        'flex items-center justify-center gap-1.5 rounded-lg border transition-colors active:scale-95',
        'bg-amber-500/15 border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/25 disabled:opacity-40',
        general ? 'px-2.5 py-1.5 text-[11px] font-bold' : 'w-9 h-9',
        className,
      )}
    >
      <BellRing className={general ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
      {general && <T>Zvonček</T>}
    </button>
  )
}
